import { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { RHFValidations } from '../../constants/global-constants';
import parsePasswordValidationMap from '../../_helpers/passwordValidation';
import PasswordInputBox from '../../controls/PasswordInputBox';
import { acceptInvite, InviteInfo, lookupInvite } from '../../services/dashboard-service';

type FORMDATA = {
  password: string;
  confirmPassword: string;
};

/**
 * Landing page for a copied invitation link: the invitee chooses a password
 * and can then sign in. No temporary password is needed.
 * @returns - React component for the accept-invite screen.
 */
export default function AcceptInviteComponent(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const methods = useForm<FORMDATA>({ mode: 'all' });
  const { handleSubmit, watch } = methods;

  useEffect(() => {
    if (!token) {
      setLinkError('This invitation link is not valid.');
      return;
    }
    lookupInvite(token)
      .then(({ data }) => setInvite(data))
      .catch((e) =>
        setLinkError(e?.response?.data?.message || 'This invitation link is not valid.')
      );
  }, [token]);

  /**
   * Sets the password and sends the user to sign in.
   * @param data - Form values.
   */
  const onSubmit = async (data: FORMDATA): Promise<void> => {
    setLoading(true);
    try {
      await acceptInvite(token, data.password, data.confirmPassword);
      toast.success('Your account is ready. Please sign in with your new password.');
      navigate(`/auth/login?email=${encodeURIComponent(invite?.userName || '')}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Unable to accept the invitation.');
    } finally {
      setLoading(false);
    }
  };

  const confirmPasswordValidation = parsePasswordValidationMap(
    'confirm password',
    watch('password')
  );

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Welcome to TelosAir
      </div>
      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="text-xl dark:text-white text-black font-semibold">Accept invitation</div>
        {linkError && (
          <div className="mt-4 text-gray-400">
            <div role="alert">{linkError}</div>
            <button
              type="button"
              className="mt-6 text-primary-500 hover:underline"
              onClick={() => navigate('/auth/login')}
            >
              Go to sign in
            </button>
          </div>
        )}
        {!linkError && !invite && (
          <div className="flex justify-center mt-8">
            <ClipLoader size={24} color="#888" />
          </div>
        )}
        {invite && (
          <>
            <div className="text-gray-400 mt-1">
              {invite.firstName ? `Hi ${invite.firstName}, choose` : 'Choose'} a password for{' '}
              <span className="dark:text-white text-black">{invite.userName}</span>.
            </div>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <FormProvider {...methods}>
              <form className="mt-8 2xl:mt-12" onSubmit={handleSubmit(onSubmit)}>
                <PasswordInputBox
                  label="Password"
                  idname="password"
                  autoComplete="new-password"
                  validationObject={RHFValidations.password}
                />
                <PasswordInputBox
                  label="Confirm Password"
                  idname="confirmPassword"
                  autoComplete="new-password"
                  validationObject={confirmPasswordValidation}
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full relative mt-12 text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg flex justify-center items-center text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
                >
                  {loading ? <ClipLoader size={20} color="#fff" /> : 'Set Password'}
                </button>
              </form>
            </FormProvider>
          </>
        )}
      </div>
    </div>
  );
}
